import React, { useEffect, useState } from 'react';
import { Table, TableProps, Modal, PaginationProps, Button } from 'antd';
import { Link } from 'react-router-dom';
import { getRequest, deleteRequest, ResponseDataType } from 'api';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { CreateClass } from '../Create';
import { EditClass } from '../Edit';
import { ClassInfo } from '../Information';
import { ClassLevelSelect } from 'Components';
const { confirm } = Modal;

interface ClassDataType {
  classId: number;
  className: string;
  classLevel: string;
  classOrder: number;
  description: string | null;
}

export const ClassList = () => {
  const [classList, setClassList] = useState<ClassDataType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<ClassDataType | null>(null);
  const [classLevelsKey, setClassLevelsKey] = useState<number>(0);

  const schoolIdStr = localStorage.getItem('schoolId');
  const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

  const columns: TableProps<ClassDataType>['columns'] = [
    {
      title: 'Mã lớp',
      dataIndex: 'classId',
      key: 'classId',
      align: 'center',
    },
    {
      title: 'Tên lớp',
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: 'Khối lớp',
      dataIndex: 'classLevel',
      key: 'classLevel',
      align: 'center',
    },
    {
      title: 'Thứ tự',
      dataIndex: 'classOrder',
      key: 'classOrder',
      align: 'center',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'description',
      key: 'description',
      align: 'center',
    },
    {
      title: 'Hành động',
      dataIndex: 'classId',
      key: 'action',
      render: (classId: number, record: ClassDataType) => (
        <div>
          <span
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => {
              setSelectedClassId(classId);
              setIsDetailModalOpen(true);
            }}
          >
            Chi tiết
          </span>
          <span
            style={{ color: '#52c41a', marginLeft: '10px', cursor: 'pointer' }}
            onClick={() => {
              setSelectedClass(record);
              setIsEditModalOpen(true);
            }}
          >
            Sửa
          </span>
          <span
            style={{ color: 'red', marginLeft: '10px', cursor: 'pointer' }}
            onClick={() => showDeleteConfirm(classId)}
          >
            Xóa
          </span>
        </div>
      ),
      align: 'center',
    },
  ];

  const fetchClasses = async () => {
    if (!schoolId || isNaN(schoolId)) {
      toast.error('Không tìm thấy schoolId. Vui lòng đăng nhập lại.');
      return;
    }

    setLoading(true);

    const queryParams = new URLSearchParams({
      schoolId: schoolId.toString(),
      page: currentPage.toString(),
      perPage: pageSize.toString(),
      sort: 'asc',
      sortBy: 'className',
    });

    if (selectedClassLevel) {
      queryParams.append('classLevel', selectedClassLevel);
    }

    const url = `/cms/class/get-class-by-class-level?${queryParams.toString()}`;

    const response: ResponseDataType<{
      list: ClassDataType[];
      total: number;
    }> = await getRequest(url);

    if (response?.code === 200) {
      setClassList(response.info?.list || []);
      setTotalItems(response.info?.total || 0);
    } else {
      setClassList([]);
      setTotalItems(0);
      toast.error(response?.msg || 'Lỗi khi tải danh sách lớp');
    }

    setLoading(false);
  };

  const showDeleteConfirm = (classId: number) => {
    confirm({
      title: 'Bạn có thực sự muốn xóa lớp này?',
      icon: <ExclamationCircleFilled />,
      content: 'Bạn sẽ không thể hoàn tác sau khi xóa.',
      okText: 'Xác nhận',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        const url = `/cms/class/${classId}`;
        const response: ResponseDataType<{}> = await deleteRequest(url);

        if (response?.code === 200) {
          toast.success('Xóa lớp thành công');
          fetchClasses();
          setClassLevelsKey((prev) => prev + 1);
        } else {
          toast.error(response?.msg || 'Xóa lớp thất bại');
        }
      },
    });
  };

  const handlePageChange: PaginationProps['onChange'] = (page, size) => {
    setCurrentPage(page);
    setPageSize(size || 50);
  };

  useEffect(() => {
    fetchClasses();
  }, [currentPage, pageSize, selectedClassLevel]);

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <ClassLevelSelect
            key={classLevelsKey}
            value={selectedClassLevel}
            onChange={(value) => {
              setSelectedClassLevel(value);
              setCurrentPage(1);
            }}
            placeholder="Chọn khối"
            style={{ width: 160 }}
          />
        </div>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Thêm lớp học
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={classList}
        rowKey="classId"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          pageSizeOptions: ['10', '20', '30', '50', '100'],
          showSizeChanger: true,
          onChange: handlePageChange,
        }}
      />

      <Modal
        title="Thêm lớp học"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        {schoolId && (
          <CreateClass
            schoolId={schoolId}
            onSuccess={() => {
              fetchClasses();
              setClassLevelsKey((prev) => prev + 1);
            }}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </Modal>

      <Modal
        title="Chi tiết lớp học"
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedClassId(undefined);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        {selectedClassId && <ClassInfo classId={selectedClassId} />}
      </Modal>

      <Modal
        title="Chỉnh sửa lớp học"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setSelectedClass(null);
        }}
        footer={null}
        destroyOnClose
      >
        {schoolId && selectedClass && (
          <EditClass
            classData={selectedClass}
            schoolId={schoolId}
            onSuccess={() => {
              fetchClasses();
              setClassLevelsKey((prev) => prev + 1);
            }}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedClass(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};
